import React, { useEffect, useRef, useState } from "react";
import { FaSearch, FaUserCircle } from "react-icons/fa";
import { getRequest } from "../helpers/api.helper";
import { useNavigate } from "react-router-dom";
import { UPLOAD_URL } from "../utils/constants";
import { shouldBlurProfileImage } from "../utils/imagePrivacy";

type SearchByMobileProps = {
  initialMobile?: string;
  autoSearch?: boolean;
};

const SearchByMobile: React.FC<SearchByMobileProps> = ({
  initialMobile = "",
  autoSearch = false,
}) => {
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [error, setError] = useState("");
  const didAutoSearch = useRef(false);

  const navigate = useNavigate();

  const formatDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const getPaymentStatusLabel = (profile: any) => {
    const raw =
      profile?.paymentStatus ?? profile?.planType ?? profile?.accountType ?? "";
    const value = String(raw).toLowerCase();
    if (value.includes("red")) return "Red Zone";
    if (
      value.includes("paid") ||
      value.includes("completed") ||
      value === "1"
    ) {
      return "Paid Profile";
    }
    if (value.includes("free") || value.includes("pending") || value === "0") {
      return "Free Profile";
    }
    return raw ? String(raw) : "Free Profile";
  };

  const formatAmount = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `₹${num}`;
  };

  const handleSearch = async () => {
    if (!mobile.trim()) {
      setError("Please enter mobile number.");
      return;
    }

    setError("");
    setLoading(true);
    setProfiles([]);

    try {
      const response = await getRequest(`/user/profile/by-mobile/${mobile}`);

      if (response.success) {
        setProfiles(Array.isArray(response.data) ? response.data : []);
      } else if (response?.isDeleted) {
        setError("This profile is deleted");
      } else {
        setError("No profile found for this mobile number.");
      }
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialMobile) return;
    const cleaned = initialMobile.replace(/\D/g, "").slice(0, 10);
    setMobile(cleaned);
  }, [initialMobile]);

  useEffect(() => {
    if (!autoSearch || didAutoSearch.current) return;
    if (!mobile.trim()) return;
    didAutoSearch.current = true;
    handleSearch();
  }, [autoSearch, mobile]);

  return (
    <div className="w-full py-6">
      <div className="flex flex-col sm:flex-row items-end gap-3 px-2">
        <div className="w-full sm:max-w-md">
          <label className="block text-green-700 text-sm font-medium mb-1">
            Enter Mobile Number
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="tel"
            placeholder="Eg: 9876543210"
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData("text");
              setMobile(pastedText.replace(/\D/g, "").slice(0, 10));
            }}
            className="w-full border-2 border-green-500 rounded-md px-4 py-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-green-400 focus:outline-none"
            maxLength={10}
          />
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className={`flex items-center gap-2 px-6 py-2 rounded-md text-white font-semibold transition ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          <FaSearch size={22} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-600 text-sm font-medium mt-2">{error}</p>
      )}

      {/* Profile Result */}
      {profiles.length > 0 && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {profiles.map((profile) => {
            const ageValue =
              typeof profile.age === "object"
                ? profile.age?.display || ""
                : profile.age || "";
            const locationParts = [
              profile.city,
              profile.district,
              profile.state,
              profile.country,
            ].filter(Boolean);
            const locationText =
              locationParts.length > 0 ? locationParts.join(", ") : "N/A";
            const paymentStatusLabel = getPaymentStatusLabel(profile);
            const paymentStatusLower = paymentStatusLabel.toLowerCase();
            const paymentStatusClass = paymentStatusLower.includes("red")
              ? "bg-red-100 text-red-700"
              : paymentStatusLower.includes("paid")
                ? "bg-green-100 text-green-700"
                : "bg-emerald-100 text-emerald-700";


            return (
              <div
                key={profile._id || profile.martialId}
                className="rounded-2xl border border-gray-100 bg-white px-6 py-7 shadow-[0_12px_28px_rgba(15,23,42,0.08)]"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gray-200 rounded-full overflow-hidden flex items-center justify-center">
                    {profile.image ? (
                      (() => {
                        const blurPhoto = shouldBlurProfileImage(
                          profile.imagePrivacy,
                        );

                        return (
                          <img
                            src={
                              UPLOAD_URL + "/users/profile_pic/" + profile.image
                            }
                            alt={profile.fullName}
                            className={`w-full h-full object-cover ${
                              blurPhoto ? "blur-sm scale-110 brightness-90" : ""
                            }`}
                          />
                        );
                      })()
                    ) : (
                      <FaUserCircle className="text-gray-400 text-5xl" />
                    )}
                  </div>

                  <h3 className="mt-4 text-xl font-semibold text-slate-800">
                    {profile.fullName}
                  </h3>
                </div>

                <div className="mt-4 grid grid-cols-[140px,10px,1fr] gap-x-2 gap-y-2 text-sm">
                  <span className="text-slate-500 font-bold">Matrimony ID</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {profile.martialId || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">Age</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {ageValue || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">Caste</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {profile.caste || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">Location</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {locationText}
                  </span>
                  <span className="text-slate-500 font-bold">
                    Annual Income
                  </span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {profile.annualIncome || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">Bureau Name</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {profile.bureauName || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">
                    Bureau Address
                  </span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {profile.bureauAddress || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">Created Date</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {formatDate(profile.createdAt) || "N/A"}
                  </span>
                  <span className="text-slate-500 font-bold">
                    Payment Status
                  </span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${paymentStatusClass}`}
                    >
                      {paymentStatusLabel}
                    </span>
                  </span>
                  <span className="text-slate-500 font-bold">Paid Amount</span>
                  <span className="text-center">:</span>
                  <span className="text-slate-800 font-bold">
                    {formatAmount(
                      profile.registrationFees ??
                        profile.paidAmount ??
                        profile.amountPaid,
                    ) || "N/A"}
                  </span>
                </div>

                <button
                  className="mt-5 w-full bg-orange-400 hover:bg-green-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition"
                  onClick={() => {
                    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                    navigate(`/admin/profile/detail/${profile.martialId}`);
                  }}
                >
                  View Full Profile
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchByMobile;
